import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { ApkService } from '../../apk/apk.service';
import { take, takeUntil } from 'rxjs/operators';
import { Policy } from '../../apk/models/save-policy.interface';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, Message, MessageService } from 'primeng/api';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../../environments/environment';
import { ApiService } from '../../../services/api.service';
import * as moment from 'moment';
import { DialogService } from 'primeng/dynamicdialog';
import { CtaLoadingDialogComponent } from './cta-loading-dialog/cta-loading-dialog.component';
import { TrustBadgeService } from '../../../services/trust-badge.service';
import { Store } from '@ngxs/store';
import {
  ApplyAffiliationDiscount,
  GetPolicyByNumber,
  ResetOfferState,
  SignOffer,
} from '../../policy-creator/store/offer.action';
import { ConfirmSignaturePayload } from '../../../../interfaces/idefend/confirm-signature-payload.interface';
import { OfferState } from '../../policy-creator/store/offer.state';
import { CacheService } from 'src/app/services/cache.service';

@Component({
  selector: 'app-buy',
  templateUrl: './buy.component.html',
  styleUrls: ['./buy.component.scss'],
  providers: [DialogService, ConfirmationService],
})
export class BuyComponent implements OnInit, AfterViewInit, OnDestroy {
  typeOfPolicy = 'invoice';
  ctaFormGroup: UntypedFormGroup;
  msgs: Message[];
  signing: boolean;
  isSodanLoading$ = this.apkService.isSodanLoading$;
  isSodanLoaded$ = this.apkService.isSodanLoaded$;
  destroy$: Subject<void> = new Subject();
  userEngaged = false;

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private apkService: ApkService,
    private apiService: ApiService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private trustBadgeService: TrustBadgeService,
    public dialogService: DialogService,
    @Inject(DOCUMENT) private document: Document,
    private cacheService: CacheService,
  ) {}

  engageUser(value: boolean) {
    if (this.isSodanLoaded$.getValue() === true) {
      this.router.navigateByUrl('/apk/main');
      return;
    }

    this.userEngaged = value;

    this.isSodanLoading$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (value === false) {
        this.router.navigateByUrl('/apk/main');
      }
    });
  }

  ngAfterViewInit(): void {
    this.route.fragment.subscribe((fragment) => {
      const element = this.document.getElementById(fragment);
      if (element) {
        element.scrollIntoView({ behavior: 'auto' });
      }
    });

    this.handleAffiliationLink();

    // Clear any existing offer state when component initializes
    this.store.dispatch(new ResetOfferState());
  }

  ngOnInit(): void {
    this.initializeForm();
    this.apkService.setSodanReplyHeads();
    this.apkService.setBaseDataForCalculations();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initTrustBadge(): void {
    this.trustBadgeService
      .load({
        name: 'app-component',
        src: `https://widgets.trustedshops.com/js/${environment.trustBadgeId}.js`,
      })
      .pipe(take(1))
      .subscribe();
  }

  handleAffiliationLink(): void {
    this.activeRoute.paramMap.subscribe((params: ParamMap) => {
      const id = params.get('affiliationLink');
      if (id) {
        const affiliationCode = environment.baseUrl + '/cta/buy-gap/' + id;
        this.apiService
          .checkAffiliationLink(id)
          .pipe(take(1))
          .subscribe(
            (res) => {
              this.store.dispatch(
                new ApplyAffiliationDiscount(res.link, res.discount),
              );

              /*if (this.cookieService.get('gap-cookie-allowed') === 'allow') {
                this.cookieService.set('affiliationLink', affiliationCode, {
                  sameSite: 'Strict',
                  expires: moment().add(14, 'd').toDate(),
                });
                this.cookieService.set('affiliationDiscount', res.discount, {
                  sameSite: 'Strict',
                  expires: moment().add(14, 'd').toDate(),
                });
              } else {
                sessionStorage.setItem('affiliationLink', affiliationCode);
                sessionStorage.setItem('affiliationDiscount', res.discount);
              }*/
            },
            (error: HttpErrorResponse) => {
              if (error.status === 404) {
                this.router.navigateByUrl(`/cta/buy-gap/`);
              }
            },
          );
      }
    });
  }

  selectTypeOfPolicy(type): void {
    this.typeOfPolicy = type;
  }

  initializeForm(): void {
    this.ctaFormGroup = this.fb.group({
      uploadDocPolicyNo: [null],
      readCalculationPolicyNo: [null],
      policyNo: [null, [Validators.required]],
      smsCode: [null, [Validators.required]],
    });
  }

  async handleDocumentsUpload(): Promise<void> {
    this.msgs = [];
    const policyNo = this.ctaFormGroup.get('uploadDocPolicyNo').value;
    // this.router.navigateByUrl(`/cta/upload/${policyNo}`);
    await this.router.navigate([`/cta/upload/${policyNo}`], {
      queryParams: { classicGap: environment.classicGap },
    });
  }

  handlePolicyRead(): void {
    this.msgs = [];
    const policyNo = this.ctaFormGroup.get('readCalculationPolicyNo').value;

    if (policyNo) {
      this.apkService
        .getPolicy(policyNo)
        .pipe(take(1))
        .subscribe(
          (res: Policy) => {
            const policyData: Policy = res;
            this.router.navigateByUrl('/apk', {
              state: { step: 1, policyData },
            });
          },
          (err: HttpErrorResponse) => {
            this.messageService.add({
              severity: 'error',
              summary: 'iDefend error',
              detail: err.error.message,
              life: 5000,
            });
          },
        );
    }
  }

  handleSignPolicy(): void {
    this.signing = true;
    const ref = this.dialogService.open(CtaLoadingDialogComponent, {});
    this.msgs = [];
    const policyNo = this.ctaFormGroup.get('policyNo').value;
    const payloadDto: ConfirmSignaturePayload = {
      confirmationCode: this.ctaFormGroup.get('smsCode').value,
      confirmedOn: moment().format('YYYY-MM-DDTHH:mm:ssZ'),
    };

    this.store.dispatch(new ResetOfferState());
    this.messageService.clear();

    this.store
      .dispatch(new GetPolicyByNumber(policyNo))
      .pipe(take(1))
      .subscribe(() => {
        const offer = this.store.selectSnapshot(OfferState.offerLocked);

        if (offer?.id) {
          if (offer.signatureState !== 'waiting_for_confirmation') {
            this.signing = false;
            ref.close();
            this.messageService.add({
              severity: 'error',
              summary: 'iDefend error',
              detail: 'Policy already confirmed',
              life: 5000,
            });
            return;
          }

          this.store
            .dispatch(new SignOffer(offer.id, payloadDto))
            .pipe(take(1))
            .subscribe(
              () => {
                this.signing = false;
                ref.close();
                this.messageService.add({
                  severity: 'success',
                  summary: 'Podposano polisę: ',
                  detail: offer.number,
                  life: 5000,
                });
                if (environment.production) {
                  this.initTrustBadge();
                  this.trustBadgeService.invokeBadge({
                    tsCheckoutOrderNr: offer.number,
                    tsCheckoutBuyerEmail: offer.client.insured.email,
                    tsCheckoutOrderAmount: (offer.premiumMax / 100).toString(),
                    tsCheckoutOrderCurrency: 'PLN',
                    tsCheckoutOrderPaymentType: 'WIRE TRANSFER',
                    tsCheckoutOrderEstDeliveryDate: new Date().toString(),
                  });
                }
              },
              (err) => {
                this.signing = false;
                ref.close();
                this.messageService.add({
                  severity: 'error',
                  summary: 'iDefend error',
                  detail:
                    err.error?.iDefendResponse?.data?.violations?.[0]
                      ?.message || err.error?.iDefendResponse?.data?.detail,
                  life: 5000,
                });
              },
            );
          return;
        }

        this.signing = false;
        ref.close();
        this.messageService.add({
          severity: 'error',
          summary: 'iDefend error',
          detail: 'Provided policy number not found',
          life: 5000,
        });
      });
  }
}
